import { Input } from "~/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./tabs";
import { useRef, useState } from "react";

export function MultipleVersionInput({
  name,
  value,
  placeholder = "Eingeben",
  label,
  errors,
  maxChars,
  onChange,
  onBlur,
  onEnterKey,
  hideEnglish = false,
}: {
  name: string[];
  value: string[];
  placeholder?: string;
  label: string[];
  errors?: {
    german?: string;
    english?: string;
  };
  maxChars?: number;
  onChange: (value: string[]) => void;
  onBlur?: () => void;
  onEnterKey?: () => void;
  hideEnglish?: boolean;
}) {
  // Ensure all values are strings (handle undefined/null)
  const safeValue = value.map((v) => (v ?? "") as string);

  const [showMaxCharsWarning, setShowMaxCharsWarning] = useState<
    Record<number, boolean>
  >({});
  const warningTimeouts = useRef<Record<number, NodeJS.Timeout>>({});

  const triggerMaxCharsWarning = (index: number) => {
    setShowMaxCharsWarning((prev) => ({ ...prev, [index]: true }));

    if (warningTimeouts.current[index]) {
      clearTimeout(warningTimeouts.current[index]);
    }

    warningTimeouts.current[index] = setTimeout(() => {
      setShowMaxCharsWarning((prev) => ({ ...prev, [index]: false }));
    }, 3000);
  };

  const [activeTab, setActiveTab] = useState(name[0]);

  // If hideEnglish is true, only show the first (German) input without tabs UI
  if (hideEnglish) {
    const errorMessage = errors?.german;
    const hasError = !!errorMessage;

    return (
      <div className="flex flex-col gap-2">
        <Input
          type="text"
          name={name[0]}
          placeholder={placeholder}
          value={safeValue[0] ?? ""}
          onChange={(e) => {
            if (maxChars && e.target.value.length > maxChars) {
              triggerMaxCharsWarning(0);
              return;
            }

            onChange([e.target.value, ""]);
          }}
          onBlur={onBlur}
          onKeyDown={(e) => {
            if (e.key === "Enter" && onEnterKey) {
              e.preventDefault();
              onEnterKey();
            }
          }}
          {...(hasError && { "aria-invalid": true })}
          {...(errorMessage && { "aria-describedby": `${name[0]}-error` })}
          variant="outline"
        />
        <div className="flex justify-between gap-8">
          <span>
            {errorMessage && (
              <p id={`${name[0]}-error`} className="text-destructive text-sm text-red-500">
                {errorMessage}
              </p>
            )}

            {showMaxCharsWarning[0] && (
              <p className="text-sm text-amber-600">
                Maximale Zeichenanzahl überschritten
              </p>
            )}
          </span>
          {maxChars ? (
            <p className="text-neutral-darkest text-right">
              {(safeValue[0] ?? "").length}/{maxChars}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Tabs className={"gap-4"} defaultValue={name[0]}>
        <TabsList>
          {label.map((l, index) => (
            <TabsTrigger key={index} value={name[index]}>
              {l}
            </TabsTrigger>
          ))}
        </TabsList>
        {label.map((l, index) => {
          const isGerman = index === 0;
          const errorMessage = isGerman ? errors?.german : errors?.english;
          const hasError = !!errorMessage;
          const isActiveTab = activeTab === name[index];

          return (
            <TabsContent key={index} value={name[index]}>
              <div className="flex flex-col gap-2">
                <Input
                  type="text"
                  name={name[index]}
                  placeholder={placeholder}
                  value={safeValue[index] ?? ""}
                  onChange={(e) => {
                    if (maxChars && e.target.value.length > maxChars) {
                      triggerMaxCharsWarning(index);
                      return;
                    }

                    const updated = [...safeValue];
                    updated[index] = e.target.value;
                    onChange(updated);
                  }}
                  {...(isActiveTab && hasError && { "aria-invalid": true })}
                  {...(isActiveTab &&
                    errorMessage && {
                    "aria-describedby": `${name[index]}-error`,
                  })}
                  onBlur={onBlur}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && onEnterKey) {
                      e.preventDefault();
                      onEnterKey();
                    }
                  }}
                  variant="outline"
                />
                <div className="flex justify-between gap-8">
                  <span>
                    {isActiveTab && errorMessage && (
                      <p className="text-destructive text-sm text-red-500">
                        {errorMessage}
                      </p>
                    )}

                    {isActiveTab && showMaxCharsWarning[index] && (
                      <p className="text-sm text-amber-600">
                        Maximale Zeichenanzahl überschritten
                      </p>
                    )}
                  </span>
                  {maxChars ? (
                    <p className="text-neutral-darkest text-right">
                      {(safeValue[index] ?? "").length}/{maxChars}
                    </p>
                  ) : null}
                </div>
              </div>
            </TabsContent>)
        })}
      </Tabs>
    </div>
  );
}
